import { ref } from "vue";

import type { ContentfulItem } from "@tmi/types/entities";

/*****************************************************************************
 * Hover-to-acknowledge state for "NEW" content markers.
 *
 * Distinct from read state (content-read-state.ts):
 * - read    = the user has opened the content (persisted, drives many UIs)
 * - acked   = the user has hovered over the item, dimming the "NEW" badge,
 *             but the row/card itself remains in lists; click to actually
 *             read.
 *
 * Persisted to localStorage so a hover on the notifications dropdown also
 * clears the NEW badge on the matching ContentPreview card across the app
 * (and survives reloads). Per-browser by nature of localStorage.
 *****************************************************************************/

const storageKey = 'tmi:notifications-acknowledged';

function load(): Set< string > {
    try {
        const raw = globalThis.localStorage?.getItem( storageKey );
        if ( !raw ) return new Set();
        const parsed = JSON.parse( raw );
        return Array.isArray( parsed ) ? new Set( parsed.filter( ( k: unknown ): k is string => typeof k === 'string' ) ) : new Set();
    } catch {
        return new Set();
    }
}

function save( keys: Set< string > ): void {
    try {
        globalThis.localStorage?.setItem( storageKey, JSON.stringify( Array.from( keys ) ) );
    } catch {
        // localStorage unavailable (private mode, quota, SSR) — degrade silently
    }
}

const acknowledgedKeys = ref< Set< string > >( load() );

export function acknowledgementKey( content: ContentfulItem | undefined, contentType: string | undefined ): string | undefined {
    if ( !contentType ) return undefined;
    const id = content?.contentfulId ?? content?.slug;
    if ( !id ) return undefined;
    return `${ contentType }:${ id }`;
}

export function useNotificationAcknowledgement() {
    function isAcknowledged( content: ContentfulItem | undefined, contentType: string | undefined ): boolean {
        const key = acknowledgementKey( content, contentType );
        return !!key && acknowledgedKeys.value.has( key );
    }

    function acknowledge( content: ContentfulItem | undefined, contentType: string | undefined ): void {
        const key = acknowledgementKey( content, contentType );
        if ( !key || acknowledgedKeys.value.has( key ) ) return;
        const next = new Set( acknowledgedKeys.value );
        next.add( key );
        acknowledgedKeys.value = next;
        save( next );
    }

    return {
        acknowledge,
        isAcknowledged,
    };
}
